// src/pages/admin/ElectionDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL_ELECTIONS } from '../../../utils/constants';
// import ProtectedRoute from '../../../components/auth/ProtectedRoute';
import ElectionProtectedRoute from '../../../components/auth/ElectionProtectedRoute';

const ElectionDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    totalElections: 0,
    activeElections: 0,
    totalVotes: 0
  });

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('election_token');

      let totalElections = 0;
      let activeElections = 0;
      let totalVotes = 0;

      for (const school of schools) {
        // Fetch elections
        const electionsRes = await fetch(`${API_BASE_URL_ELECTIONS}/elections/school/${school.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const electionsData = await electionsRes.json();

        if (electionsData.success) {
          totalElections += electionsData.data.length;

          // Count active elections
          const now = new Date();
          electionsData.data.forEach(election => {
            const start = new Date(election.startDate);
            const end = new Date(election.endDate);
            if (now >= start && now <= end && election.isActive) {
              activeElections++;
            }
          });

          // Fetch vote stats for each election
          for (const election of electionsData.data) {
            const statsRes = await fetch(`${API_BASE_URL_ELECTIONS}/votes/election/${election.id}/stats`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const statsData = await statsRes.json();
            if (statsData.success) {
              totalVotes += statsData.data.totalVotes || 0;
            }
          }
        }
      }

      setStats({ totalElections, activeElections, totalVotes });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    if (schools.length > 0) {
      fetchStats();
    }
  }, [schools]);

  useEffect(() => {
    checkAuth();
    fetchSchools();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('election_token');
    const userData = localStorage.getItem('election_user');

    if (!token || !userData) {
      navigate('/admin/election-login');
      return;
    }

    setUser(JSON.parse(userData));
  };

  const fetchSchools = async () => {
    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/auth/assigned-schools`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSchools(data.data);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('election_token');
    localStorage.removeItem('election_user');
    navigate('/admin/election-login');
  };

  if (loading) {
    return (
      <ElectionProtectedRoute>
        <div className="flex items-center justify-center min-h-screen bg-black">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-400 font-semibold">Loading Dashboard...</p>
          </div>
        </div>
      </ElectionProtectedRoute>
    );
  }

  return (
    <ElectionProtectedRoute>
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 border-b border-red-900/30 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <span className="text-red-500">🗳️</span>
                  Election Dashboard
                </h1>
                <p className="text-gray-400 mt-1">Welcome back, <span className="text-red-400 font-semibold">{user?.fullName}</span></p>
              </div>
              <div className="flex items-center gap-4">
                <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${user?.role === 'SUPER_ADMIN'
                    ? 'bg-red-900/30 text-red-400 border-red-500/50'
                    : 'bg-gray-800 text-gray-300 border-gray-600'
                  }`}>
                  {user?.role === 'SUPER_ADMIN' ? '👑 Super Admin' : '🏫 Institution Admin'}
                </span>
                <button
                  onClick={handleLogout}
                  className="px-5 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {user?.role === 'SUPER_ADMIN' && (
              <>
                <Link to="/admin/manage-schools" className="group">
                  <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 to-black border border-red-900/30 hover:border-red-500/50 rounded-xl shadow-lg p-6 transition-all duration-300 transform hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative flex items-center justify-between">
                      <div>
                        <div className="text-3xl mb-2">🏫</div>
                        <h3 className="text-white font-bold text-lg">Manage Schools</h3>
                        <p className="text-gray-400 text-sm mt-1">Create & configure schools</p>
                      </div>
                      <svg className="w-6 h-6 text-red-500 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>

                <Link to="/admin/manage-admins" className="group">
                  <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 to-black border border-red-900/30 hover:border-red-500/50 rounded-xl shadow-lg p-6 transition-all duration-300 transform hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative flex items-center justify-between">
                      <div>
                        <div className="text-3xl mb-2">👥</div>
                        <h3 className="text-white font-bold text-lg">Manage Admins</h3>
                        <p className="text-gray-400 text-sm mt-1">Assign admin privileges</p>
                      </div>
                      <svg className="w-6 h-6 text-red-500 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              </>
            )}

            <Link to="/admin/manage-elections" className="group">
              <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 to-black border border-red-900/30 hover:border-red-500/50 rounded-xl shadow-lg p-6 transition-all duration-300 transform hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex items-center justify-between">
                  <div>
                    <div className="text-3xl mb-2">🗳️</div>
                    <h3 className="text-white font-bold text-lg">Manage Elections</h3>
                    <p className="text-gray-400 text-sm mt-1">Create & monitor elections</p>
                  </div>
                  <svg className="w-6 h-6 text-red-500 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-xl opacity-20 group-hover:opacity-40 blur transition duration-300"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-red-900/30 rounded-lg border border-red-500/30">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="text-3xl font-black text-red-500">{schools.length}</div>
                </div>
                <div className="text-gray-400 text-sm font-medium">Assigned Schools</div>
              </div>
            </div>

            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-xl opacity-20 group-hover:opacity-40 blur transition duration-300"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-red-900/30 rounded-lg border border-red-500/30">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div className="text-3xl font-black text-red-500">{stats.totalElections}</div>
                </div>
                <div className="text-gray-400 text-sm font-medium">Total Elections</div>
              </div>
            </div>

            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-green-700 rounded-xl opacity-20 group-hover:opacity-40 blur transition duration-300"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black border border-green-900/30 rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-green-900/30 rounded-lg border border-green-500/30">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-3xl font-black text-green-500">{stats.activeElections}</div>
                </div>
                <div className="text-gray-400 text-sm font-medium">Active Elections</div>
              </div>
            </div>

            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-xl opacity-20 group-hover:opacity-40 blur transition duration-300"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-red-900/30 rounded-lg border border-red-500/30">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                  </div>
                  <div className="text-3xl font-black text-red-500">{stats.totalVotes.toLocaleString()}</div>
                </div>
                <div className="text-gray-400 text-sm font-medium">Total Votes Cast</div>
              </div>
            </div>
          </div>

          {/* Schools List */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-red-500">🏫</span>
                {user?.role === 'SUPER_ADMIN' ? 'All Schools' : 'Your Assigned Schools'}
              </h2>
              {schools.length > 0 && (
                <span className="px-4 py-2 bg-red-900/30 text-red-400 rounded-lg text-sm font-semibold border border-red-500/30">
                  {schools.length} {schools.length === 1 ? 'School' : 'Schools'}
                </span>
              )}
            </div>

            {schools.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-red-900/20 rounded-full mb-4 border border-red-500/30">
                  <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">No schools assigned</h3>
                <p className="text-gray-400 max-w-md mx-auto">
                  {user?.role === 'SUPER_ADMIN'
                    ? 'Create schools to get started with election management'
                    : 'Please contact a super admin to assign schools to your account'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {schools.map((school) => (
                  <div key={school.id} className="group relative">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-300"></div>
                    <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 group-hover:border-red-500/50 rounded-xl p-6 transition-all duration-300">
                      <div className="flex items-start gap-4 mb-4">
                        {school.logo ? (
                          <img src={school.logo} alt={school.name} className="w-14 h-14 rounded-lg object-cover border-2 border-red-500/30" />
                        ) : (
                          // <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center text-white font-bold text-xl border-2 border-red-500/30">
                          //   {school.shortName || school.name.charAt(0)}
                          // </div>
                          <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center text-white font-bold text-xl border-2 border-red-500/30">
                            <span className="text-center text-ellipsis overflow-hidden whitespace-nowrap max-w-full text-[clamp(0.8rem,2vw,1.25rem)]">
                              {school.shortName || school.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-white text-lg truncate">{school.name}</h3>
                          {school.shortName && (
                            <p className="text-sm text-gray-400 mt-1">{school.shortName}</p>
                          )}
                        </div>
                      </div>

                      {/* <Link 
                        to={`/admin/schools/${school.id}/manage`}
                        className="block w-full text-center px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg text-sm font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red-600/30"
                      >
                        Manage School
                      </Link> */}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ElectionProtectedRoute>
  );
};

export default ElectionDashboard;